import { tryAssistantTrainingAnswer } from '@/lib/assistantTrainingKnowledge';
import {
  normalizeAssistantText,
  inferServicePreferenceFromText,
} from '@/lib/assistant/assistantTextUtils';
import {
  tryCoreBusinessInfoFaq,
  formatOwnerContactBlock,
} from '@/lib/assistant/assistantBusinessFaq';
import {
  detectFrustrationLevel,
  conversationalComplaintLevel,
  frustrationPrefix,
  tryConversationalStandalone,
  humanFallbackCopy,
} from '@/lib/assistant/assistantConversation';
import {
  rankProductMatches,
  INGREDIENT_ALIASES,
  PRODUCT_FAMILY_ALIASES,
  bestProductMatchForAssistantQuery,
  resolveProductsByIds,
} from '@/lib/assistant/assistantProductSearch';
import { tryDeliveryFreeTextIntents } from '@/lib/assistant/assistantDelivery';
import { matchOrderStepFreeText, trySocialLayer } from '@/lib/assistant/assistantSocial';
import {
  applyContextualSuggestionLayer,
  applySessionMemoryToAnswer,
  tryCommerceLayerIntents,
  tryBurgerBundleCompositionAnswer,
  tryProductMenuIntents,
  isContinueShoppingShortcutIntent,
  isAffirmativeComplementReply,
  isShortUpsellDeclineReply,
  pickRecommendationProducts,
  extractCustomizationNote,
  formatOrderNoteFromCustomizationSnippet,
  hasOrderInstructionIntent,
  hasCustomizationInstructionShape,
  isCompositionConsultationOnly,
} from '@/lib/assistant/assistantProducts';

export {
  normalizeAssistantText,
  inferServicePreferenceFromText,
  detectFrustrationLevel,
  conversationalComplaintLevel,
  frustrationPrefix,
};
export {
  rankProductMatches,
  INGREDIENT_ALIASES,
  PRODUCT_FAMILY_ALIASES,
};
export { applyContextualSuggestionLayer };

/**
 * @param {object} menuData — respuesta de GET /menu/:id
 * @param {string} rawText
 * @param {{ skipSocialLayer?: boolean, skipConversationalLayer?: boolean, flowContext?: { currentStep?: string, servicePreference?: 'delivery'|'pickup'|null, lastSuggestedProductIds?: string[], sessionMemory?: object, cartItemCount?: number }, treatAsOrderPartyStep?: boolean }} [options]
 */
export function buildFreeTextAnswer(menuData, rawText, options = {}) {
  const trimmed = String(rawText || '').trim();
  const memory = options.flowContext?.sessionMemory || {};
  const inner = buildFreeTextAnswerInner(menuData, rawText, options, trimmed, memory);
  const withMem = applySessionMemoryToAnswer(inner, memory, trimmed);
  return applyContextualSuggestionLayer(withMem, menuData, options.flowContext);
}

function buildFreeTextAnswerInner(menuData, rawText, options, trimmed, memory) {
  const business = menuData?.business || {};
  const products = menuData?.products || [];
  const promos = (menuData?.promos || []).filter((p) => p.is_active !== false);

  const n = normalizeAssistantText(trimmed);

  const servicePreference =
    inferServicePreferenceFromText(n) ??
    (memory.servicePreference === 'delivery' || memory.servicePreference === 'pickup'
      ? memory.servicePreference
      : null) ??
    options.flowContext?.servicePreference ??
    null;

  const defaultFallback = ['order_hint', 'catalog', 'main'];

  if (!trimmed) {
    return {
      text: 'Escribí tu consulta en una oración.',
      products: null,
      fallbackKeys: defaultFallback,
    };
  }

  if (isContinueShoppingShortcutIntent(n, trimmed)) {
    return {
      text: 'Dale 👌 Seguimos con tu pedido. Elegí una opción o escribí lo que buscás por nombre 👇',
      products: null,
      fallbackKeys: ['order_hint', 'categories_hint', 'popular_hint', 'promo_hint', 'cheap_hint', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  if (!options.skipConversationalLayer) {
    const convo = tryConversationalStandalone(
      menuData,
      trimmed,
      n,
      business,
      defaultFallback,
      servicePreference
    );
    if (convo) return convo;
  }

  if (!options.skipSocialLayer) {
    const social = trySocialLayer(
      menuData,
      trimmed,
      n,
      defaultFallback,
      { flowContext: options.flowContext },
      (remainder, innerOpts) => buildFreeTextAnswer(menuData, remainder, innerOpts)
    );
    if (social) return social;
  }

  if (options.flowContext?.currentStep === 'order' || options.treatAsOrderPartyStep === true) {
    const orderHit = matchOrderStepFreeText(n, trimmed);
    if (orderHit) {
      return {
        text: orderHit.ack,
        orderChoice: orderHit.choice,
        products: null,
        fallbackKeys: defaultFallback,
      };
    }
  }

  const cartN = Number(options.flowContext?.cartItemCount);
  if (Number.isFinite(cartN) && cartN > 0 && isShortUpsellDeclineReply(n, trimmed)) {
    return {
      text: 'Perfecto 👍 seguimos',
      products: null,
      fallbackKeys: ['order_hint', 'catalog', 'main'],
      containmentIncluded: true,
      frustrationLevel: 0,
    };
  }

  const lastProdIds =
    memory.lastProducts?.length > 0
      ? memory.lastProducts
      : options.flowContext?.lastSuggestedProductIds || [];
  if (isAffirmativeComplementReply(n, trimmed) && lastProdIds.length > 0) {
    const resolvedCtx = resolveProductsByIds(products, lastProdIds);
    const picks = pickRecommendationProducts(products, resolvedCtx, 8, memory, menuData, n);
    if (picks.length) {
      return {
        text: 'Dale 👌 Estas opciones combinan bien con lo que estabas viendo:',
        products: picks,
        fallbackKeys: defaultFallback,
      };
    }
  }

  const { ranked, bestProduct } = bestProductMatchForAssistantQuery(trimmed, products);

  if (
    bestProduct &&
    hasOrderInstructionIntent(n) &&
    hasCustomizationInstructionShape(n, trimmed) &&
    !isCompositionConsultationOnly(n, trimmed)
  ) {
    const noteRaw = extractCustomizationNote(trimmed);
    if (noteRaw) {
      const orderNoteAppend = formatOrderNoteFromCustomizationSnippet(noteRaw);
      const display = orderNoteAppend
        ? `${orderNoteAppend.charAt(0).toLowerCase()}${orderNoteAppend.slice(1)}`
        : '';
      return {
        text: display ? `Perfecto 👌 lo dejo anotado: ${display}.` : 'Perfecto 👌 lo dejo anotado en el pedido.',
        products: [bestProduct],
        orderNoteAppend,
        fallbackKeys: defaultFallback,
      };
    }
  }

  const burgerBundleAns = tryBurgerBundleCompositionAnswer(
    n,
    products,
    menuData,
    memory,
    bestProduct,
    options.flowContext
  );
  if (burgerBundleAns) return burgerBundleAns;

  const commerceHit = tryCommerceLayerIntents(
    n,
    trimmed,
    products,
    business,
    defaultFallback,
    ranked,
    bestProduct,
    options.flowContext,
    menuData
  );
  if (commerceHit) return commerceHit;

  const coreBizFaq = tryCoreBusinessInfoFaq(n, business, defaultFallback);
  if (coreBizFaq) return coreBizFaq;

  if (
    /\b(dueno|dueño|dueña|encargad|hablar con|contacto|whatsapp|wsp)\b/.test(n) &&
    !/\b(cuanto|cuánto|precio|cuesta|horario)\b/.test(n)
  ) {
    return {
      text: formatOwnerContactBlock(business),
      products: null,
      fallbackKeys: business.whatsapp ? ['whatsapp', 'catalog', 'main'] : defaultFallback,
    };
  }

  const deliveryHit = tryDeliveryFreeTextIntents(n, trimmed, business, defaultFallback, servicePreference);
  if (deliveryHit) return deliveryHit;

  const productHit = tryProductMenuIntents({
    n,
    trimmed,
    menuData,
    business,
    products,
    promos,
    defaultFallback,
    ranked,
    bestProduct,
    memory,
    options,
  });
  if (productHit) return productHit;

  const trainingAns = tryAssistantTrainingAnswer(menuData, trimmed, n, defaultFallback);
  if (trainingAns) return trainingAns;

  return {
    text: humanFallbackCopy(),
    products: null,
    fallbackKeys: ['order_hint', 'promo_hint', 'cheap_hint', 'catalog', 'main'],
  };
}
